/**
 * A focus window's controls after its pill's hairline (opened-window rule
 * 1): the item's ⋯ (`item-menu.tsx`), Show large on a side frame (plan 13),
 * then ⛶ Focus in a desktop preview (Part 05 Q1) or ⤢ Maximize / back to
 * size in Focus. A right-click inside the window opens the same menu as ⋯
 * (`item-context-menu.ts`).
 */
import { Button, Icons } from "@/components/ui";
import type { FrameItemMenuProps } from "@/components/shapes/shared/frame-renderer-context";
import { useT } from "@/i18n";
import { focusMessages } from "@/i18n/messages/focus";
import { wallMessages } from "@/i18n/messages/wall";
import { enterExplicitFocus } from "@/lib/focus-presentation";
import { useAboveWall } from "@/lib/focus/use-above-wall";
import {
  makeFocusFrameMain,
  toggleFocusedFrameWindow,
} from "@/lib/focus-window-presentation";
import { isTauri } from "@/lib/is-tauri";
import { usePresentationStore } from "@/stores/presentation-store";
import { ItemMenuButton } from "./item-menu";

/** A desktop preview: above the wall, where Focus differs from Open. */
function usePreviewing(): boolean {
  const aboveWall = useAboveWall();
  return isTauri() && aboveWall;
}

/** ⤢ grows the Focus window to the screen; Minimize2 puts it back. */
function MaximizeButton({ frameId }: { frameId: string }) {
  const t = useT(focusMessages);
  const grown = usePresentationStore((state) => {
    const window = state.presentation?.focusWindow;
    return window?.frameId === frameId && window.mode !== "normal";
  });
  const label = grown ? t("restoreSize") : t("maximize");
  const Icon = grown ? Icons.Minimize : Icons.Maximize;
  return (
    <Button
      type="button"
      variant="ghost"
      size="iconSm"
      aria-label={label}
      title={label}
      data-testid={`focus-window-maximize-${frameId}`}
      onClick={() => toggleFocusedFrameWindow(frameId)}
    >
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );
}

/** Shows a side frame large: it becomes the main frame, beside the column. */
function ShowLargeButton({ frameId }: { frameId: string }) {
  const t = useT(focusMessages);
  const side = usePresentationStore((state) => {
    const presentation = state.presentation;
    return (
      presentation?.variant === "focus" &&
      presentation.customFocusLayout?.kind === "rounds" &&
      presentation.frameIds.indexOf(frameId) > 0
    );
  });
  if (!side) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="iconSm"
      aria-label={t("showLarge")}
      title={t("showLarge")}
      data-testid={`focus-window-show-large-${frameId}`}
      onClick={() => makeFocusFrameMain(frameId)}
    >
      <Icons.PanelLeft className="h-3.5 w-3.5" />
    </Button>
  );
}

export function FocusWindowMenu(props: FrameItemMenuProps) {
  const previewing = usePreviewing();
  const tWall = useT(wallMessages);
  const frameId = props.frame.frameId;
  return (
    <>
      <ItemMenuButton {...props} />
      <ShowLargeButton frameId={frameId} />
      {previewing ? (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label={tWall("focus")}
          title={tWall("focus")}
          data-testid={`focus-window-focus-${frameId}`}
          onClick={() => enterExplicitFocus([frameId])}
        >
          <Icons.Scan className="h-3.5 w-3.5" />
        </Button>
      ) : (
        <MaximizeButton frameId={frameId} />
      )}
    </>
  );
}
